import { memo } from 'react'

interface TableFooterProps {
  tableId: string
  hasColumns: boolean
  fkCount: number
  idxCount: number
  fkTitle: string
  idxTitle: string
  readOnly?: boolean
  onOpenFk?: (tableId: string, event: React.MouseEvent<HTMLButtonElement>) => void
  onOpenIdx?: (tableId: string, event: React.MouseEvent<HTMLButtonElement>) => void
}

/**
 * Elo de corrente. As duas metades se sobrepoem sobre uma barra central — o
 * desenho anterior eram dois arcos soltos que, a 12px, liam como um rabisco.
 */
function LinkIcon() {
  return (
    <svg className="ft-svg" width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M10 6.5H7.5a5.5 5.5 0 0 0 0 11H10"
        stroke="currentColor"
        strokeWidth="2.1"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M14 6.5h2.5a5.5 5.5 0 0 1 0 11H14"
        stroke="currentColor"
        strokeWidth="2.1"
        strokeLinecap="round"
        fill="none"
      />
      <path d="M8.25 12h7.5" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" />
    </svg>
  )
}

function DatabaseIcon() {
  return (
    <svg className="ft-svg" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
      <ellipse cx="6" cy="3" rx="4.5" ry="1.8" stroke="currentColor" strokeWidth="1.2" />
      <path d="M1.5 3 L1.5 9 C1.5 9.99 3.57 10.8 6 10.8 C8.43 10.8 10.5 9.99 10.5 9 L10.5 3" stroke="currentColor" strokeWidth="1.2" fill="none" />
      <path d="M1.5 6 C1.5 6.99 3.57 7.8 6 7.8 C8.43 7.8 10.5 6.99 10.5 6" stroke="currentColor" strokeWidth="1.2" fill="none" />
    </svg>
  )
}

const badgeStyle: React.CSSProperties = {
  minWidth: 16,
  height: 16,
  padding: '0 5px',
  borderRadius: 999,
  background: 'rgba(15,23,42,.08)',
  color: 'var(--text)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 9,
  fontWeight: 'bold',
  boxShadow: '0 1px 3px rgba(15,23,42,.16), inset 0 0 0 1px rgba(99,102,241,.12)',
}

const btnStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 5,
  padding: '3px 7px',
  border: '1px solid var(--col-border)',
  borderRadius: 5,
  background: 'none',
  cursor: 'pointer',
  fontSize: 10,
  color: 'var(--text-muted)',
  fontFamily: 'inherit',
  transition: 'all .12s',
}

export const TableFooter = memo(function TableFooter({
  tableId,
  hasColumns,
  fkCount,
  idxCount,
  fkTitle,
  idxTitle,
  readOnly = false,
  onOpenFk,
  onOpenIdx,
}: TableFooterProps) {
  return (
    <div
      className="etfoot"
      style={{
        display: 'flex',
        flexShrink: 0,
        alignItems: 'center',
        background: 'var(--tbl-body)',
        borderTop: hasColumns ? 'none' : '1px solid var(--col-border)',
        padding: '3px 4px',
        gap: 4,
      }}
    >
      {!readOnly ? (
      <button
        className="etfoot-btn"
        data-action="fk-detail"
        data-tbl={tableId}
        title={fkTitle}
        style={btnStyle}
        onClick={(event) => {
          event.stopPropagation()
          onOpenFk?.(tableId, event)
        }}
      >
        <span className="ft-icon" style={{ display: 'inline-flex', width: 12, height: 12, alignItems: 'center', justifyContent: 'center' }}>
          <LinkIcon />
        </span>
        <span className="ft-label" style={{ lineHeight: 1 }}>FK</span>
        <span className="ft-badge" style={badgeStyle}>{fkCount}</span>
      </button>
      ) : null}

      {!readOnly ? (
      <button
        className="etfoot-btn"
        data-action="idx-detail"
        data-tbl={tableId}
        title={idxTitle}
        style={btnStyle}
        onClick={(event) => {
          event.stopPropagation()
          onOpenIdx?.(tableId, event)
        }}
      >
        <span className="ft-icon" style={{ display: 'inline-flex', width: 12, height: 12, alignItems: 'center', justifyContent: 'center' }}>
          <DatabaseIcon />
        </span>
        <span className="ft-label" style={{ lineHeight: 1 }}>IDX</span>
        <span className="ft-badge" style={badgeStyle}>{idxCount}</span>
      </button>
      ) : null}
    </div>
  )
})
